<script lang="ts" setup>
const props = defineProps({
  list: {
    required: true,
    type: Object as PropType<Partial<List & { webhooks?: Webhook[] }>>,
  },
  size: {
    default: 'md',
    type: String as PropType<UInputSize>,
  },
})

const { t } = useI18n()
const { createToast } = useToasts()
const teamsStore = useTeamsStore()
const listsStore = useListsStore()

const editing = ref(false)
const updatingList = ref(false)

const options = computed((): USelectMenuOption[] => {
  return teamsStore.webhooks.map((webhook) => {
    return {
      icon: getIcon('webhook'),
      id: webhook.id,
      label: webhook.name,
    }
  })
})

const selectedLists = computed<USelectMenuOption[]>(
  {
    get: () => {
      return (
        (props?.list?.webhooks || [])
          .map(webhook => options.value.find(option => option.id === webhook.id))
          .filter(Boolean) as USelectMenuOption[]
      )
    },
    set: async (value) => {
      updatingList.value = true

      // Get the list of attached webhooks ids
      const attachedWebhooksIds = props?.list?.webhooks?.map(webhook => webhook.id) || []

      if (value.length > (props?.list?.webhooks?.length || 0)) {
        // Attaching a webhook
        try {
          for (const option of value) {
            if (!attachedWebhooksIds.includes(option.id) && props.list?.id) {
              await teamsStore.updateTeamWebhook({
                listsToAdd: [props.list.id],
                webhookId: option.id,
              })
            }
          }

          createToast({ message: 'listUpdated' })
        }
        catch {
          createToast({
            message: 'somethingWentWrong',
            status: 'error',
          })
        }
      }
      else {
        // Detaching a webhook
        try {
          for (const attachedWebhookId of attachedWebhooksIds) {
            if (!value.find(option => option.id === attachedWebhookId) && props.list?.id) {
              await teamsStore.updateTeamWebhook({
                listsToRemove: [props.list.id],
                webhookId: attachedWebhookId,
              })
            }
          }

          createToast({ message: 'listUpdated' })
        }
        catch {
          createToast({
            message: 'somethingWentWrong',
            status: 'error',
          })
        }
      }

      await listsStore.fetchLists({
        page: 1,
        size: 500,
      })

      updatingList.value = false
    },
  },
)
</script>

<template>
  <USelectMenu
    v-if="!editing"
    v-model="selectedLists"
    class="w-52"
    :icon="getIcon('webhook')"
    :loading="updatingList"
    multiple
    :options="options"
    searchable
    :searchable-placeholder="t('searchYourWebhooks')"
    :size="size"
    :ui-menu="{ container: 'min-w-48' }"
  >
    <template #label>
      <span class="truncate">
        {{ selectedLists.length ? `${selectedLists.length} ${selectedLists.length > 1 ? t('webhooksAttached') : t('webhookAttached')}` : t('attachWebhooks') }}
      </span>
    </template>

    <template #option="{ option }">
      <div class="flex items-center gap-2">
        <UIcon
          class="size-5 flex-shrink-0"
          :name="option.icon"
        />

        <span class="text-sm">
          {{ option.label }}
        </span>

        <UChip
          v-if="option.chip"
          class="ml-1 [&>*]:!text-white"
          color="red"
          size="xl"
          :text="option?.chip > 9 ? '9+' : option.chip"
          variant="subtle"
        />
      </div>
    </template>
  </USelectMenu>
</template>

<i18n lang="json">
{
  "en": {
    "attachWebhooks": "Attach webhooks",
    "searchYourWebhooks": "Search your webhooks",
    "webhookAttached": "webhook attached",
    "webhooksAttached": "webhooks attached"
  },
  "es": {
    "attachWebhooks": "Añadir webhooks",
    "searchYourWebhooks": "Buscar en tus webhooks",
    "webhookAttached": "webhook añadido",
    "webhooksAttached": "webhooks añadidos"
  },
  "fr": {
    "attachWebhooks": "Attacher des webhooks",
    "searchYourWebhooks": "Rechercher dans vos webhooks",
    "webhookAttached": "webhook attaché",
    "webhooksAttached": "webhooks attachées"
  }
}
</i18n>
